import { createSignal } from 'solid-js';

// What the Accounts page is showing beside the holdings, or nothing.
//
// Two things can occupy that panel and they answer different questions: an
// account's blotter is "what did I buy in here", a holding is "tell me about
// this position". Two signals rather than one slot, so each has a name — but
// opening either closes the other, because there is one panel and a reader
// should not have to guess which of two things is in it.

const [openAccountId, setOpenAccountId] = createSignal<string | null>(null);

/**
 * One account, filling the page, or null for the grid of all of them.
 *
 * A separate idea from the panel signals below: those decide what sits *beside*
 * the accounts, this decides whether you are looking at all of them or at one.
 * Four cards answer "where is my money"; one account filling the screen answers
 * "what is in this one", and that question wants the room.
 */
const [focusedAccountId, setFocusedAccountId] = createSignal<string | null>(null);

export { focusedAccountId };

export const focusAccount = (accountId: string): void => {
    setFocusedAccountId(accountId);
};

export const unfocusAccount = (): void => {
    setFocusedAccountId(null);
};
const [openHolding, setOpenHolding] = createSignal<{ accountId: string; symbol: string } | null>(null);

export { openAccountId, openHolding };

/** Clicking the account already open closes it, so the same button both shows
    and hides and there is no separate way out. */
export const toggleTransactions = (accountId: string): void => {
    setOpenHolding(null);
    setOpenAccountId((current) => (current === accountId ? null : accountId));
};

export const closeTransactions = (): void => {
    setOpenAccountId(null);
};

/** Same fund in two accounts is two positions — the cost base, the return of
    capital carried against it and the buys behind it are all per account — so
    the key is the pair, not the symbol. */
export const showHolding = (accountId: string, symbol: string): void => {
    setOpenAccountId(null);
    setOpenHolding((current) => (current?.accountId === accountId && current.symbol === symbol ? null : { accountId, symbol }));
};

export const closeHolding = (): void => {
    setOpenHolding(null);
};

export const isHoldingOpen = (accountId: string, symbol: string): boolean => {
    const open = openHolding();
    return open?.accountId === accountId && open.symbol === symbol;
};
